/**
 * Translate our Firestore EventDoc into the shape FullCalendar expects.
 * Recurrence: schema is `none | daily | weekly` plus `recurrenceUntil`.
 * FullCalendar's rrule plugin handles expansion natively, so we map
 * straight to RRULE. Per-instance overrides aren't supported in v1 —
 * editing a recurring event modifies the source, all occurrences
 * update together (see docs/v1-ui-deferrals.md).
 */

import type { EventInput } from '@fullcalendar/core';
import type { Timestamp } from 'firebase/firestore';
import type { EventDoc } from '@/schemas';
import { instantToFloatingIso } from '@/lib/timeZone';

export type EventWithId = EventDoc & { id: string };

/** Two visual styles, distinguishing the two `kind`s. */
export const EVENT_COLOR = '#E97C3C';   // accent-500 — agenda items
export const REMINDER_COLOR = '#3F8985'; // teal — only speaks during window

function tsToDate(ts: Timestamp | undefined): Date | null {
  if (!ts) return null;
  if (typeof (ts as { toDate?: unknown }).toDate === 'function') {
    return (ts as { toDate: () => Date }).toDate();
  }
  return null;
}

/** `tz` is the ELDER's account timezone. Event instants are rendered at
 * their account-tz wall-clock (as offset-less "floating" ISO strings), so the
 * calendar shows the elder's times regardless of the caregiver's browser
 * zone — FullCalendar runs in local/floating mode (see #141). */
export function toFcEvent(ev: EventWithId, tz: string): EventInput | null {
  const start = tsToDate(ev.startsAt);
  if (!start) return null;
  const end = tsToDate(ev.endsAt) ?? undefined;

  const isRecurring = !!(ev.recurrence && ev.recurrence !== 'none');
  const kind = ev.kind ?? 'event';
  const hasNotifications = !!(ev.notifications && ev.notifications.length > 0);
  const colour = kind === 'reminder' ? REMINDER_COLOR : EVENT_COLOR;
  const base: EventInput = {
    id: ev.id,
    title: ev.title,
    allDay: ev.allDay,
    backgroundColor: colour,
    borderColor: colour,
    textColor: '#ffffff',
    // All events are draggable; the page-level eventDrop / eventResize
    // handlers detect a recurring source and prompt the caregiver
    // (this occurrence vs. all occurrences) before mutating.
    extendedProps: {
      sourceEvent: ev,
      kind,
      hasNotifications,
    },
  };

  if (isRecurring) {
    const until = tsToDate(ev.recurrenceUntil);
    // dtstart as an account-tz floating ISO (no Z, no offset). A Date object
    // would make rrule.js read UTC components and emit occurrences at the
    // wrong wall-clock; a no-offset string preserves the account-tz wall-clock
    // hour across DST in the renderer (FullCalendar is in local/floating mode).
    const dtstart = instantToFloatingIso(start, tz);
    base.rrule = {
      freq: ev.recurrence,           // 'daily' | 'weekly'
      dtstart,
      ...(until ? { until: instantToFloatingIso(until, tz) } : {}),
    };
    if (ev.excludedDates && ev.excludedDates.length > 0) {
      // exdate strings must match the OCCURRENCE start (which inherits
      // hh:mm:ss from dtstart), not midnight — rrule.js compares the full
      // floating ISO. Reuse dtstart's time part so a 09:00 exclusion matches
      // the 09:00 occurrence. (Original bug discovered 2026-05-21.)
      const t = dtstart.split('T')[1] ?? '00:00:00';
      base.exdate = ev.excludedDates.map((d) => `${d}T${t}`);
    }
    // For recurring events, FullCalendar still wants `duration` so each
    // generated occurrence renders with the correct length. The instant delta
    // is the true span, independent of tz.
    if (end) {
      const ms = end.getTime() - start.getTime();
      const totalMin = Math.max(1, Math.round(ms / 60000));
      base.duration = { minutes: totalMin };
    }
  } else {
    // Floating account-tz wall-clock strings (incl. all-day, whose date
    // component must be the account-tz date, not the browser's).
    base.start = instantToFloatingIso(start, tz);
    if (end) base.end = instantToFloatingIso(end, tz);
  }

  return base;
}

export function toFcEvents(events: EventWithId[], tz: string): EventInput[] {
  return events
    .map((ev) => toFcEvent(ev, tz))
    .filter((e): e is EventInput => e !== null);
}
